import React, { useMemo, useRef, useState } from "react";
import clsx from "clsx";

import { ArrowPanel, IconComponent } from "@app/core";
import { FeaturedCategoriesModule } from "@app/api/modules/featured-categories/featured-categories.module";
import Typography from "@app/components/typography";
import ChevronLeftIcon from "@assets/icons/chevron-left.svg";
import ChevronRightIcon from "@assets/icons/chevron-right.svg";
import Flicking, { ChangedEvent, DIRECTION, ReachEdgeEvent } from "@egjs/react-flicking";
import { useDebouncedViewPortSize } from "@app/util/hooks/use-viewport-size";

import styles from "./featured-categories-component.module.scss";

export interface IFeaturedCategoriesProps {
  featuredCategoriesModule: FeaturedCategoriesModule;
}

export const FeaturedCategories = (props: IFeaturedCategoriesProps) => {
  const { categoryItems, title } = props.featuredCategoriesModule;
  const carousel = useRef<Flicking>(null);

  const [{ width: viewportWidth }] = useDebouncedViewPortSize(300, { leading: true });

  const [showNextButton, setShowNextButton] = useState<boolean>(true);
  const [showPrevButton, setShowPrevButton] = useState<boolean>(false);

  const showControls = useMemo(() => {
    if (!viewportWidth) return false;

    const slides = getCarouselViewSlides(viewportWidth);

    return slides < categoryItems.length;
  }, [viewportWidth, categoryItems.length]);

  const showFullCards = categoryItems.some((ci) => (ci.image || ci.fallbackImgUrl) && ci.link.title.length > 0);

  const selectedItemChanged = (e: ChangedEvent) => {
    if (!showNextButton && e.index < e.prevIndex) {
      setShowNextButton(true);
    }
    if (!showPrevButton && e.index > e.prevIndex) {
      setShowPrevButton(true);
    }
  };

  const edgeReached = (e: ReachEdgeEvent) => {
    if (e.direction === DIRECTION.NEXT) {
      setShowNextButton(false);
    }
    if (e.direction === DIRECTION.PREV) {
      setShowPrevButton(false);
    }
  };

  return (
    <div className={styles.featuredCategories}>
      <div className="uk-container">
        {title && <Typography tag="h2">{title}</Typography>}
        <div className={styles.wrapper} data-uk-margin>
          <>
            {showControls && showPrevButton && (
              <div className={clsx(styles.buttonWrapper)}>
                <button type="button" className={styles.sliderButton} onClick={() => carousel?.current?.prev()}>
                  <div className={styles.icon}>
                    <IconComponent icon={ChevronLeftIcon} strokeColor="white" size="14px" />
                  </div>
                </button>
              </div>
            )}
            <Flicking
              viewportTag="div"
              cameraTag="div"
              cameraClass={styles.slider}
              horizontal={true}
              bound
              moveType="freeScroll"
              preventClickOnDrag
              preventDefaultOnDrag
              align={"prev"}
              inputType={["touch", "mouse"]}
              ref={carousel}
              onChanged={selectedItemChanged}
              onReachEdge={edgeReached}
            >
              {categoryItems?.map((item, index) => (
                <div key={index} className={styles.category}>
                  <ArrowPanel
                    {...item}
                    link={{
                      title: item.link.title,
                      url: item.link.url,
                      newTab: item.link.url.includes("/out/category/") || item.link.newTab
                    }}
                    rounded
                    variant={showFullCards ? "categoryBig" : "categorySmall"}
                    featuredFilter={item.featuredFilter}
                  />
                </div>
              ))}
            </Flicking>
            {showControls && showNextButton && (
              <div className={clsx(styles.buttonWrapper, styles.buttonWrapperNext)}>
                <button type="button" className={styles.sliderButton} onClick={() => carousel?.current?.next()}>
                  <div className={styles.icon}>
                    <IconComponent icon={ChevronRightIcon} strokeColor="white" size="14px" />
                  </div>
                </button>
              </div>
            )}
          </>
        </div>
      </div>
    </div>
  );
};

const carouselBreakPoints = {
  mobileOneSlide: 330,
  mobileTwoSlides: 480,
  mobileThreeSlides: 620,
  mobileFourSlides: 640,
  desktopTwoSlides: 710,
  desktopThreeSlides: 920,
  desktopFourSlides: 1150,
  desktopFiveSlides: 1400
};

const getCarouselViewSlides = (width: number) => {
  if (width < carouselBreakPoints.mobileOneSlide) return 1;
  if (width < carouselBreakPoints.mobileTwoSlides) return 2;
  if (width < carouselBreakPoints.mobileThreeSlides) return 3;
  if (width < carouselBreakPoints.mobileFourSlides) return 4;
  if (width < carouselBreakPoints.desktopTwoSlides) return 2;
  if (width < carouselBreakPoints.desktopThreeSlides) return 3;
  if (width < carouselBreakPoints.desktopFourSlides) return 4;
  return 5;
};
